/**
 * The sign-in / register form's state, as data.
 *
 * The whole reason this is not three `useState` calls inside the component: the
 * two modes share a form, and someone who typed an email, tapped "Register" and
 * found the field empty would have to type it again. So the mode is a field of
 * the same record as the values, and switching it is a transition that carries
 * everything the user typed across — including the name, which sign-in does not
 * use but register will want back if they toggle again.
 *
 * Validation is here for the same reason it is not left to the server: a
 * round trip to be told the password is seven characters long is a round trip
 * spent, and the rule (eight, from `emailAndPassword.minPasswordLength`) is
 * fixed and knowable on the device.
 */

export type AuthMode = "sign-in" | "register";

export interface AuthForm {
  mode: AuthMode;
  email: string;
  password: string;
  name: string;
}

/** Mirrors `minPasswordLength` in `src/server/auth.ts`. */
export const MIN_PASSWORD = 8;

export const EMPTY_FORM: AuthForm = { mode: "sign-in", email: "", password: "", name: "" };

/**
 * Flip between the two modes, keeping every typed value.
 *
 * Returns the same object when the mode already matches, so a re-render caused
 * by tapping the tab you are already on does not clear a submit error.
 */
export function setMode(form: AuthForm, mode: AuthMode): AuthForm {
  if (form.mode === mode) return form;
  return { ...form, mode };
}

export function setField(form: AuthForm, field: "email" | "password" | "name", value: string): AuthForm {
  return { ...form, [field]: value };
}

/**
 * The first thing wrong with the form, in words, or null if it can be submitted.
 *
 * One message at a time and in reading order: a form that lights up three
 * complaints at once on a phone in dim light is a form nobody finishes.
 */
export function validate(form: AuthForm): string | null {
  const email = form.email.trim();
  if (email === "") return "Enter your email address.";
  // Deliberately not a full RFC address grammar — the server owns that verdict.
  // This only catches the typo the user can see, on the device, for free.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "That does not look like an email address.";
  if (form.mode === "register" && form.name.trim() === "") {
    return "Enter the name the table should see.";
  }
  if (form.password === "") return "Enter your password.";
  if (form.mode === "register" && form.password.length < MIN_PASSWORD) {
    return `Passwords need at least ${MIN_PASSWORD} characters.`;
  }
  return null;
}

/** The trimmed values an action gets. Passwords are never trimmed — they are literal. */
export function credentialsOf(form: AuthForm): { email: string; password: string; name: string } {
  return { email: form.email.trim(), password: form.password, name: form.name.trim() };
}
